import { useCallback, useEffect, useState } from "react";
import { CreditCard, Check, ShieldCheck, Download } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const naira = (n) => "₦" + Number(n || 0).toLocaleString("en-NG", { maximumFractionDigits: 0 });
const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—");
const STATUS = {
    active: ["Active", "bg-emerald-50 text-emerald-700"],
    exempt: ["Exempted by platform", "bg-klein-50 text-klein"],
    grace: ["Grace period", "bg-amber-50 text-amber-700"],
    locked: ["Locked — payment due", "bg-red-50 text-red-700"],
};

function StatusCard({ s }) {
    const [label, tone] = STATUS[s.status] || [s.status, ""];
    return (
        <div className="bg-white border border-ink/10 p-6 sm:p-8" data-testid="billing-status-card">
            <span className={`text-xs px-2.5 py-1 font-semibold rounded-full ${tone}`} data-testid="billing-status">{label}</span>
            <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-6 text-sm">
                <div><div className="text-ink/50">Active staff</div><div className="mt-1 font-display font-bold text-2xl" data-testid="billing-staff-count">{s.staff_count}</div></div>
                <div><div className="text-ink/50">Your size plan</div><div className="mt-1 font-semibold">{s.required_plan?.name}</div></div>
                <div><div className="text-ink/50">{s.status === "active" ? "Paid until" : s.status === "exempt" ? "Exempt until" : "Grace ends"}</div>
                    <div className="mt-1 font-semibold" data-testid="billing-until">{fmt(s.status === "active" ? s.paid_until : s.status === "exempt" ? s.exception_until : s.grace_ends_at)}</div></div>
                <div><div className="text-ink/50">Days left</div><div className="mt-1 font-display font-bold text-2xl" data-testid="billing-days-left">{s.days_left}</div></div>
            </div>
        </div>
    );
}

export default function Billing() {
    const [s, setS] = useState(null);
    const [payments, setPayments] = useState([]);
    const [busy, setBusy] = useState("");
    const [code, setCode] = useState("");
    const [disc, setDisc] = useState(null);
    const applyCode = async (e) => {
        e.preventDefault();
        try {
            const { data } = await api.post("/billing/discount/check", { code });
            setDisc(data);
            toast.success(`${data.percent}% off applied`);
        } catch (err) {
            setDisc(null);
            toast.error(formatApiError(err.response?.data?.detail) || "That code isn't valid");
        }
    };

    const load = useCallback(async () => {
        const [a, b] = await Promise.all([api.get("/billing/status"), api.get("/billing/payments").catch(() => ({ data: [] }))]);
        setS(a.data);
        setPayments(b.data || []);
    }, []);
    useEffect(() => { load().catch(() => toast.error("Could not load billing")); }, [load]);
    const focus = new URLSearchParams(window.location.search).get("upgrade");
    useEffect(() => {
        if (s && focus) document.querySelector(`[data-testid="billing-plan-${focus}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, [s, focus]);

    const pay = async (plan, mode = "renew") => {
        setBusy(plan.id);
        try {
            const { data } = await api.post("/billing/paystack/initialize", { plan_id: plan.id, mode, discount_code: mode === "renew" && disc ? disc.code : "", callback_url: `${window.location.origin}/billing/callback` });
            window.location.href = data.authorization_url;
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not start payment");
            setBusy("");
        }
    };

    const receipt = async (p) => {
        try {
            const { data } = await api.get(`/billing/payments/${p.reference}/receipt`, { responseType: "blob" });
            const url = URL.createObjectURL(data);
            const a = Object.assign(document.createElement("a"), { href: url, download: `receipt_${p.receipt_no || p.reference}.pdf` });
            a.click();
            setTimeout(() => URL.revokeObjectURL(url), 1000);
        } catch {
            toast.error("Could not download the receipt");
        }
    };

    if (!s) return <div className="p-10 text-ink/40">Loading…</div>;
    return (
        <div className="p-4 sm:p-8 lg:p-12 max-w-6xl" data-testid="billing-page">
            <div className="overline">Subscription</div>
            <h1 className="mt-2 font-display font-extrabold text-4xl sm:text-5xl text-ink flex items-center gap-3"><CreditCard className="w-9 h-9 text-klein" /> Billing</h1>
            <p className="mt-2 text-sm text-ink/60">Annual plans by company size, paid securely with Paystack (card, bank transfer or USSD).</p>
            <div className="mt-8"><StatusCard s={s} /></div>
            {s.first_payment && (
                <form onSubmit={applyCode} className="mt-6 flex flex-wrap items-center gap-2" data-testid="billing-discount-form">
                    <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="Discount code" maxLength={40}
                        className="border border-ink/15 bg-white px-3 py-2.5 text-sm font-mono uppercase w-52 focus:outline-none focus:border-klein" data-testid="billing-discount-input" />
                    <button type="submit" disabled={!code.trim()} className="border border-ink/20 bg-white px-4 py-2.5 text-sm hover:bg-ink/5 disabled:opacity-50" data-testid="billing-discount-apply">Apply</button>
                    {disc && (
                        <span className="text-sm text-emerald-700 font-semibold" data-testid="billing-discount-applied">
                            {disc.code}: {disc.percent}% off your first year
                            <button type="button" onClick={() => { setDisc(null); setCode(""); }} className="ml-2 text-xs text-ink/50 underline" data-testid="billing-discount-remove">Remove</button>
                        </span>
                    )}
                </form>
            )}

            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5" data-testid="billing-plans">
                {s.plans.map((p) => {
                    const tooSmall = p.max_staff != null && s.staff_count > p.max_staff;
                    const rec = p.id === s.required_plan?.id;
                    const up = s.upgrades?.[p.id];
                    return (
                        <div key={p.id} className={`bg-white border p-6 flex flex-col ${p.id === focus ? "border-klein ring-4 ring-klein/30" : rec ? "border-klein ring-2 ring-klein/20" : "border-ink/10"}`} data-testid={`billing-plan-${p.id}`}>
                            {rec && <span className="self-start text-[11px] font-bold uppercase tracking-wider text-klein">Fits your team</span>}
                            <div className="mt-1 font-display font-bold text-xl text-ink">{p.name}</div>
                            <div className="text-sm text-ink/55">{p.max_staff == null ? "Unlimited staff" : `Up to ${p.max_staff} staff`}</div>
                            <div className="mt-5" data-testid={`billing-price-${p.id}`}>
                                {disc?.prices?.[p.id] != null && !up ? (
                                    <>
                                        <div className="text-sm text-ink/40 line-through tabular-nums">{naira(p.price_ngn)}</div>
                                        <div className="font-display font-extrabold text-2xl text-emerald-700 tabular-nums leading-tight" data-testid={`billing-discounted-${p.id}`}>{naira(disc.prices[p.id])}</div>
                                    </>
                                ) : (
                                    <div className="font-display font-extrabold text-2xl text-ink tabular-nums leading-tight">{naira(p.price_ngn)}</div>
                                )}
                                <div className="mt-0.5 text-xs font-medium uppercase tracking-wider text-ink/50">per year · NGN</div>
                            </div>
                            <ul className="mt-4 space-y-1.5 text-sm text-ink/70 flex-1">
                                {["All HR, payroll & attendance features", "Staff app & reminders", "Daily backups"].map((f) => (
                                    <li key={f} className="flex gap-2"><Check className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />{f}</li>
                                ))}
                            </ul>
                            {up && (
                                <div className="mt-5 rounded-xl bg-klein/5 border border-klein/20 p-3 text-xs text-ink/70" data-testid={`billing-upgrade-info-${p.id}`}>
                                    Upgrade from {up.from_plan_name} for <b className="text-ink">{naira(up.amount_ngn)}</b>, the difference for the {up.days_left} day{up.days_left === 1 ? "" : "s"} left. Renewal date stays {fmt(up.paid_until)}.
                                </div>
                            )}
                            {up && (
                                <button onClick={() => pay(p, "upgrade")} disabled={!!busy} data-testid={`billing-upgrade-${p.id}`}
                                    className="mt-3 bg-ink text-white py-3 text-sm hover:bg-ink/85 disabled:opacity-40">
                                    {busy === p.id ? "Opening Paystack…" : `Upgrade now · ${naira(up.amount_ngn)}`}
                                </button>
                            )}
                            <button onClick={() => pay(p)} disabled={tooSmall || !!busy} data-testid={`billing-pay-${p.id}`}
                                className={`${up ? "mt-2 border border-ink/15 text-ink hover:bg-ink/5" : "mt-6 bg-klein text-white hover:bg-klein-700"} py-3 text-sm disabled:opacity-40`}>
                                {busy === p.id && !up ? "Opening Paystack…" : tooSmall ? "Too small for your team" : up ? "Or pay a full year" : s.status === "active" ? "Renew / extend 1 year" : "Pay for 1 year"}
                            </button>
                        </div>
                    );
                })}
            </div>

            <div className="mt-10 bg-white border border-ink/10 overflow-x-auto">
                <table className="w-full text-sm min-w-[560px]" data-testid="billing-payments">
                    <thead><tr className="border-b border-ink/10">{["Date", "Plan", "Amount", "Status", "Covers until", "Receipt"].map((h) => <th key={h} className="text-left px-4 py-3">{h}</th>)}</tr></thead>
                    <tbody>
                        {payments.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-ink/40">No payments yet</td></tr>}
                        {payments.map((p) => (
                            <tr key={p.reference} className="border-b border-ink/5">
                                <td className="px-4 py-3">{fmt(p.created_at)}</td><td className="px-4 py-3">{p.plan_name}</td>
                                <td className="px-4 py-3 tabular-nums">{naira(p.amount_ngn)}</td>
                                <td className="px-4 py-3 capitalize">{p.payment_status}</td><td className="px-4 py-3">{fmt(p.paid_until)}</td>
                                <td className="px-4 py-3">
                                    {p.payment_status === "paid" ? (
                                        <button onClick={() => receipt(p)} className="inline-flex items-center gap-1.5 text-xs font-semibold text-klein hover:underline" data-testid={`billing-receipt-${p.reference}`}>
                                            <Download className="w-3.5 h-3.5" /> PDF
                                        </button>
                                    ) : <span className="text-ink/30">—</span>}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <p className="mt-4 text-xs text-ink/45 flex items-center gap-1.5"><ShieldCheck className="w-4 h-4" /> Card details are handled by Paystack and never stored by us.</p>
        </div>
    );
}
